import { type AuthController } from "@firecms/core";
import { type AuthExtras } from "./types/auth";

import type { AccountCollectionId } from "./workspace/collectionIds";

export type {
  AccountCollectionId,
  EventCollectionId,
  GlobalCollectionId,
  SpaceCollectionId,
} from "./workspace/collectionIds";

export {
  formatEventCollectionId,
  getAccountPath,
  getEventPath,
  getSpacePath,
  hasAccountId,
  hasSpaceId,
  parseEventCollectionId,
  toAccountId,
  toSpaceId,
} from "./workspace/collectionIds";

export {
  getAccountIdFromEventPath,
  getEventIdFromEventPath,
  getSpaceIdFromEventPath,
  parseEventPath,
  spaceIdFromEventEntity,
  spaceIdFromEventReference,
  toEventCollectionId,
  toEventCollectionIdFromEntity,
} from "./workspace/eventIdentity";

export type { AdminMode } from "./workspace/adminMode";
export { getFirestoreBasePath, resolveAdminMode } from "./workspace/adminMode";
export { getCmsBasePath, getCmsBasePathUrl } from "./workspace/workspaceRoutes";

export const canUseAdminShell = (): boolean => true;

type PermissionsScopeId = AccountCollectionId & { eventId?: string };

/**
 * Account-level admin (invitations, account users, …).
 * Uses `extra.workspace` from the panel gate — never coarse hasAny flags.
 */
export const canAdminAccount = (
  id: AccountCollectionId,
  extra: AuthExtras | undefined
): boolean => {
  if (!extra) {
    return false;
  }
  if (extra.isSuperManager) {
    return true;
  }
  const { workspace } = extra;
  return (
    workspace?.accountId === id.accountId && workspace.isAccountManager === true
  );
};

/**
 * Event-level admin (teams, participants, event users, …).
 * Uses `extra.workspace` from the panel gate — never coarse hasAny flags.
 */
export const canAdminEvent = (
  id: PermissionsScopeId,
  extra: AuthExtras | undefined
): boolean => {
  if (!extra) {
    return false;
  }
  if (extra.isSuperManager) {
    return true;
  }
  const { workspace } = extra;
  if (!workspace || workspace.accountId !== id.accountId) {
    return false;
  }
  if (
    id.eventId &&
    workspace.eventId &&
    workspace.eventId !== id.eventId
  ) {
    return false;
  }
  if (workspace.isAccountManager) {
    return true;
  }
  if (id.eventId) {
    return workspace.isEventManager === true;
  }
  return false;
};

export const buildAccountPermissions =
  (id: PermissionsScopeId) =>
    ({ authController }: { authController: AuthController }) => {
      const extra = authController.extra as AuthExtras | undefined;
      const canEdit = canAdminEvent(id, extra);

      return {
        read: canEdit,
        edit: canEdit,
        create: canEdit,
        delete: canEdit,
      };
    };

